import { FC } from 'react'
import gql from 'graphql-tag'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import {
  GqlScreenNotificationPageDocument,
  NotificationScope,
  PageLayoutDocument,
  useDeleteAllNotificationMutation,
  useDeleteNotificationMutation,
  useGqlNotificationListQuery,
  useGqlScreenNotificationPageQuery,
  useReadNotificationMutation,
} from 'generated/graphql'
import { TabType } from '@app.components/tabs/CardTabs/CardTabs'
import removeAllScrapUpdator from '@app.feature/accounts/scrap/gql/updator/removeAllScrap'
import deleteNotificationUpdater from '@app.feature/accounts/scrap/gql/updator/removeScrap'
import useIsMobile from '@app.modules/hooks/useIsMobile'
import useIsWebview from '@app.modules/hooks/useIsWebview'
import { mobileBreakPoint } from 'app.styled/media'
import useFlutterInappwebview from 'hooks/useFlutterInappwebview'
import {
  handleClickDeleteNotificationType,
  handleClickNotificationType,
} from '../component/NotificationCard/NotificationCard.types'
import ScreenNotificationPageDesktop from './ScreenNotificationPage.desktop'
import ScreenNotificationPageMobile from './ScreenNotificationPage.mobile'
import { menuToScope, menuType } from './ScreenNotificationPage.types'

gql`
  query gqlScreenNotificationPage(
    $allFilterBy: NotificationsFilter
    $activityFilterBy: NotificationsFilter
    $channelFilterBy: NotificationsFilter
    $communityFilterBy: NotificationsFilter
    $chatFilterBy: NotificationsFilter
  ) {
    all: viewerNotifications(filterBy: $allFilterBy) {
      totalCount
    }
    activity: viewerNotifications(filterBy: $activityFilterBy) {
      totalCount
    }
    channel: viewerNotifications(filterBy: $channelFilterBy) {
      totalCount
    }
    community: viewerNotifications(filterBy: $communityFilterBy) {
      totalCount
    }
    chat: viewerNotifications(filterBy: $chatFilterBy) {
      totalCount
    }
  }
  query gqlNotificationList(
    $filterBy: NotificationsFilter
    $pagination: Pagination
  ) {
    viewerNotifications(filterBy: $filterBy, pagination: $pagination) {
      nodes {
        ...Notification_SimpleNotification
      }
      totalCount
    }
  }
`

const ScreenNotificationPage: FC = () => {
  // hooks
  const router = useRouter()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()
  const { fluterInappwebview } = useFlutterInappwebview()

  const screenNotificationQueryVariables = {
    allFilterBy: {
      scope: NotificationScope.NOTIFICATION,
    },
    activityFilterBy: {
      scope: NotificationScope.ACTIVITY_NOTIFICATION,
    },
    channelFilterBy: {
      scope: NotificationScope.CHANNEL_NOTIFICATION,
    },
    communityFilterBy: {
      scope: NotificationScope.COMMUNITY_NOTIFICATION,
    },
    chatFilterBy: {
      scope: NotificationScope.CHAT_NOTIFICATION,
    },
  }

  // quries
  const { data } = useGqlScreenNotificationPageQuery({
    variables: screenNotificationQueryVariables,
  })

  // mutations
  const readMutation = useReadNotificationMutation({
    refetchQueries: [
      {
        query: PageLayoutDocument,
        variables: {
          notificationType: NotificationScope.NOTIFICATION,
        },
      },
    ],
  })
  const deleteMutation = useDeleteNotificationMutation({
    refetchQueries: [
      {
        query: PageLayoutDocument,
        variables: {
          notificationType: NotificationScope.NOTIFICATION,
        },
      },
    ],
  })
  const deleteAllMutation = useDeleteAllNotificationMutation({
    refetchQueries: [
      {
        query: PageLayoutDocument,
        variables: {
          notificationType: NotificationScope.NOTIFICATION,
        },
      },
    ],
  })

  const selectedMenu = (router.query.tab || 'all') as menuType

  const tabs: TabType[] = [
    {
      id: 0,
      name: '전체',
      count: data?.all?.totalCount,
      href: {
        query: { tab: 'all' },
      },
    },
    {
      id: 1,
      name: '공고',
      count: data?.activity?.totalCount,
      href: {
        query: { tab: 'activity' },
      },
    },
    {
      id: 2,
      name: '채널',
      count: data?.channel?.totalCount,
      href: {
        query: { tab: 'channel' },
      },
    },
    {
      id: 3,
      name: '커뮤니티',
      count: data?.community?.totalCount,
      href: {
        query: { tab: 'community' },
      },
    },
    {
      id: 4,
      name: '채팅',
      count: data?.chat?.totalCount,
      href: {
        query: { tab: 'chat' },
      },
    },
  ]
  const currentPage = router.query.page ? +router.query.page : 1

  const notificationListQueryVariables = {
    filterBy: {
      scope: menuToScope[selectedMenu],
    },
    pagination: {
      page: currentPage,
      pageSize: 20,
    },
  }

  const query = useGqlNotificationListQuery({
    variables: notificationListQueryVariables,
    fetchPolicy: 'cache-and-network',
  })

  const handleReadNotification: handleClickNotificationType = (id) => {
    const [mutate] = readMutation
    mutate({
      variables: {
        readNotificationId: id,
      },
      onCompleted: () => {
        if (isWebview) {
          fluterInappwebview?.callHandler('reportReadNotification')
        }
      },
    })
  }

  const handleClickDeleteNotification: handleClickDeleteNotificationType = (
    id,
  ) => {
    const [mutate] = deleteMutation
    mutate({
      variables: {
        deleteNotificationId: id,
      },
      update: deleteNotificationUpdater(notificationListQueryVariables),
      refetchQueries: [
        {
          query: GqlScreenNotificationPageDocument,
          variables: screenNotificationQueryVariables,
        },
      ],
      onCompleted: () => {
        if (isWebview) {
          fluterInappwebview?.callHandler('reportRemoveNotification')
        }
      },
    })
  }

  const handleClickDeleteAll = () => {
    const [mutate] = deleteAllMutation
    mutate({
      variables: {
        scope: menuToScope[selectedMenu],
      },
      update: removeAllScrapUpdator(notificationListQueryVariables),
      refetchQueries: [
        {
          query: GqlScreenNotificationPageDocument,
          variables: screenNotificationQueryVariables,
        },
      ],
      onCompleted: () => {
        if (isWebview) {
          fluterInappwebview?.callHandler('reportRemoveAllNotifications')
        }
      },
    })
  }

  return (
    <StyledWrapper className="noti-container">
      {isMobile ? (
        <ScreenNotificationPageMobile
          tabs={tabs}
          className="mobile noti-container"
          selectedMenu={selectedMenu}
          query={query}
          onClickNotification={handleReadNotification}
          handleClickDeleteNotification={handleClickDeleteNotification}
          handleClickDeleteAll={handleClickDeleteAll}
        />
      ) : (
        <ScreenNotificationPageDesktop
          tabs={tabs}
          className="desktop noti-container"
          selectedMenu={selectedMenu}
          query={query}
          onClickNotification={handleReadNotification}
          handleClickDeleteNotification={handleClickDeleteNotification}
          handleClickDeleteAll={handleClickDeleteAll}
        />
      )}
    </StyledWrapper>
  )
}

export default ScreenNotificationPage

const StyledWrapper = styled.div`
  width: 100%;
  max-width: 1148px;
  margin: 0 auto;
  padding: 50px 24px;

  ${mobileBreakPoint} {
    padding: 0;
  }
`
